import { FC, useEffect } from "react";
import { Link } from "react-router-dom";
import Popover from "@mui/material/Popover";
import { Fade } from "@mui/material";
import IconChecksSvg from "../../../../assets/svg-fc/IconChecksSvg";
import IconChevRSvg from "../../../../assets/svg-fc/IconChevRSvg";
import NotificationCard from "../../../reusable-components/notification-card/NotificationCard";
import NotificationFilters from "../../../reusable-components/notification-filters/NotificationFilters";
import {
	NotificationEmptyState,
	NotificationLoadError,
	NotificationRowsSkeleton,
} from "../../../reusable-components/notification-states/NotificationStates";
import { useLoadMoreSentinel, useNotificationList } from "../../../../hooks/useNotificationList";
import { ROUTE_PATHS } from "../../../../routes";
import { DEFAULT_NOTIFICATION_FILTER } from "../../../../store/notifications/notificationSlice";
import { NOTIFICATION_POPOVER_LIMIT } from "../../../../store/notifications/notificationThunk";
import "../../../reusable-components/dropdown/dropdown.css";
import "./notificationDropdown.scss";

interface iNotificationDropdownProps {
	notificationOpen: boolean;
	notificationAnchorEl: HTMLElement | null;
	notificationHandleClose: () => void;
}

const NotificationDropdown: FC<iNotificationDropdownProps> = ({
	notificationOpen,
	notificationAnchorEl,
	notificationHandleClose,
}) => {
	const {
		filter,
		status,
		rows,
		counts,
		unread,
		canLoadMore,
		load,
		changeFilter,
		loadMore,
		markRead,
		markAllRead,
	} = useNotificationList("popover", NOTIFICATION_POPOVER_LIMIT);
	const sentinelRef = useLoadMoreSentinel(loadMore, notificationOpen && canLoadMore);

	useEffect(() => {
		if (notificationOpen) load(DEFAULT_NOTIFICATION_FILTER);
	}, [notificationOpen]);

	const renderList = () => {
		if (status === "loading") return <NotificationRowsSkeleton />;
		if (!rows.length) {
			return status === "failed" ? (
				<NotificationLoadError onRetry={() => changeFilter(filter)} />
			) : (
				<NotificationEmptyState filter={filter} />
			);
		}
		return (
			<div>
				{rows.map((row) => (
					<NotificationCard
						key={row.id}
						notification={row}
						onOpen={() => {
							markRead(row);
							notificationHandleClose();
						}}
					/>
				))}
			</div>
		);
	};

	return (
		<Popover
			open={notificationOpen}
			anchorEl={notificationAnchorEl}
			onClose={notificationHandleClose}
			className="dropdown notification_dropdown"
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			slots={{ transition: Fade }}
		>
			<div className="notification_dropdown_hold">
				<div className="notification_dropdown_header">
					<b>Notifications</b>
					{unread > 0 && (
						<button type="button" className="mark_read_btn" onClick={markAllRead}>
							<IconChecksSvg />
							<span>Mark all as read</span>
						</button>
					)}
				</div>
				<NotificationFilters variant="popover" filter={filter} counts={counts} onChange={changeFilter} />
				<div className="notification_dropdown_body">
					{renderList()}
					<div ref={sentinelRef} />
					{status === "loadingMore" && <NotificationRowsSkeleton rows={1} />}
				</div>
				<Link
					to={ROUTE_PATHS.NOTIFICATION}
					className="notification_dropdown_footer"
					onClick={notificationHandleClose}
				>
					View all notifications
					<IconChevRSvg />
				</Link>
			</div>
		</Popover>
	);
};

export default NotificationDropdown;
